// Helper function to generate realistic portfolio data
function generatePortfolioData(baseValue: number, isGaining: boolean, volatility: number = 0.1) {
  const now = Date.now()
  const dayMs = 24 * 60 * 60 * 1000
  const weekMs = 7 * dayMs
  const monthMs = 30 * dayMs
  const allTimeMs = 365 * dayMs

  const generateDataPoints = (period: number, points: number) => {
    const data: Array<[number, string]> = []
    const step = period / points
    let currentValue = baseValue * (isGaining ? 0.7 : 1.3) // Start lower if gaining, higher if losing

    for (let i = 0; i <= points; i++) {
      const timestamp = now - period + i * step

      // Add trend
      const trend = isGaining ? (i / points) * baseValue * 0.4 : -(i / points) * baseValue * 0.3

      // Add volatility
      const noise = (Math.random() - 0.5) * baseValue * volatility

      currentValue = Math.max(0, currentValue + trend + noise)
      data.push([Math.floor(timestamp / 1000), currentValue.toFixed(2)])
    }

    return data
  }

  return {
    day: {
      accountValueHistory: generateDataPoints(dayMs, 24),
      pnlHistory: generateDataPoints(dayMs, 24).map(
        ([ts, val]) => [ts, (parseFloat(val) - baseValue).toFixed(2)] as [number, string],
      ),
      vlm: (Math.random() * 1000000).toFixed(2),
    },
    week: {
      accountValueHistory: generateDataPoints(weekMs, 7 * 4),
      pnlHistory: generateDataPoints(weekMs, 7 * 4).map(
        ([ts, val]) => [ts, (parseFloat(val) - baseValue).toFixed(2)] as [number, string],
      ),
      vlm: (Math.random() * 5000000).toFixed(2),
    },
    month: {
      accountValueHistory: generateDataPoints(monthMs, 30),
      pnlHistory: generateDataPoints(monthMs, 30).map(
        ([ts, val]) => [ts, (parseFloat(val) - baseValue).toFixed(2)] as [number, string],
      ),
      vlm: (Math.random() * 20000000).toFixed(2),
    },
    allTime: {
      accountValueHistory: generateDataPoints(allTimeMs, 365),
      pnlHistory: generateDataPoints(allTimeMs, 365).map(
        ([ts, val]) => [ts, (parseFloat(val) - baseValue).toFixed(2)] as [number, string],
      ),
      vlm: (Math.random() * 100000000).toFixed(2),
    },
  }
}

export interface HomeContentData {
  id: string
  hero: {
    profit: string
    percentage: string
    isProfit: boolean
    period: string
    badge: {
      type: 'SHORT' | 'LONG'
      color: string
    }
  }
  tokens: Array<{
    symbol: string
    type: 'long' | 'short'
    leverage: string
    pnl: string
    percentage: string
    isProfit: boolean
  }>
  user: {
    name: string
    avatar: string
    address: string
  }
  tags: Array<{
    label: string
    bgColor: string
    textColor: string
  }>
  story: {
    content: string
    fullContent?: string
  }
  accountStats: {
    accountAssets: {
      value: string
      percentage: string
      isProfit: boolean
    }
    totalPnl: {
      value: string
      percentage: string
      isProfit: boolean
    }
    winRate: string
    profitTrades: number
    lossTrades: number
    winLossRatio: string
    largestProfit: string
    largestLoss: string
    maxDrawdown: string
    avgHold: string
    maxHold: string
  }
  portfolioData: {
    day: {
      accountValueHistory: Array<[number, string]>
      pnlHistory: Array<[number, string]>
      vlm: string
    }
    week: {
      accountValueHistory: Array<[number, string]>
      pnlHistory: Array<[number, string]>
      vlm: string
    }
    month: {
      accountValueHistory: Array<[number, string]>
      pnlHistory: Array<[number, string]>
      vlm: string
    }
    allTime: {
      accountValueHistory: Array<[number, string]>
      pnlHistory: Array<[number, string]>
      vlm: string
    }
  }
}

export const mockHomeContentList: HomeContentData[] = [
  {
    id: '1',
    hero: {
      profit: '+$9,345',
      percentage: '+54.33%',
      isProfit: true,
      period: 'Past month',
      badge: {
        type: 'SHORT',
        color: 'loss',
      },
    },
    tokens: [
      {
        symbol: 'BTC',
        type: 'short',
        leverage: '20x',
        pnl: '+$6,345.28',
        percentage: '+74.33%',
        isProfit: true,
      },
      {
        symbol: 'ETH',
        type: 'long',
        leverage: '20x',
        pnl: '-$1,345.28',
        percentage: '-14.43%',
        isProfit: false,
      },
      {
        symbol: 'SOL',
        type: 'long',
        leverage: '20x',
        pnl: '-$1,345.28',
        percentage: '-14.43%',
        isProfit: false,
      },
    ],
    user: {
      name: 'The Strike Caller',
      avatar: 'default-avatar.png',
      address: '0xF2F335D87c0633C3A19465587554722CB372FACc',
    },
    tags: [
      { label: 'Event Trader', bgColor: 'bg-green-500', textColor: 'text-green-400' },
      { label: 'High-Leverage Burst Entry', bgColor: 'bg-yellow-500/20', textColor: 'text-yellow-400' },
      { label: 'Meme Detector', bgColor: 'bg-red-500/20', textColor: 'text-red-400' },
    ],
    story: {
      content:
        '"On July 30, he spotted a rumor, dropped $200K into a $DOGA long at $0.023, and walked away four hours later with $1.7 million. He doesn\'t chase the hype"',
      fullContent: `# Chronicle of The Strike Caller

The Strike Caller, self-titled "The Lightning Trader", is said to be a legend in the memecoin battlefield. He moves like lightning—swift, decisive, and always ahead of the crowd.

## The Origin Story

He first stepped into the crypto world in early 2024, right when the meme season was heating up. While most traders were still figuring out the rules, The Strike Caller had already decoded the game.

**His Philosophy:**
- *"Speed beats size, timing beats everything"*
- *"The hype is the fuel, but the exit is the engine"*

## The DOGA Trade

On July 30th, 2024, something extraordinary happened. Social media was buzzing with whispers about a new memecoin called **$DOGA**. While others were still researching, The Strike Caller had already made his move.

### The Setup
- **Entry Point:** $0.023
- **Position Size:** $200,000
- **Market Cap:** $12M
- **Volume:** Increasing rapidly

### The Execution
Within minutes of the first major influencer tweet, he was already positioned. The trade wasn't about hope—it was about **calculated aggression**.

### The Exit
Four hours later, as $DOGA peaked at $0.19, The Strike Caller closed his position at $0.185.

**Final Numbers:**
- **Exit Price:** $0.185
- **Profit:** $1.7 million
- **ROI:** 850%
- **Time Held:** 4 hours

## What Makes Him Different

> *"He doesn't chase the hype—he **is** the hype"*

### His Trading Techniques

**Lightning Entry Method:**
- Monitors social sentiment across 50+ channels
- Uses automated alerts for momentum shifts
- Enters positions within 60 seconds of signal confirmation

**Precision Exit Strategy:**
- Sets multiple profit targets
- Never holds overnight
- Exits before euphoria peaks

**Risk Management:**
- Maximum 20% of portfolio per trade
- Always has stop-loss at -30%
- Profit-taking starts at +200%

## The Market's Verdict

*"Others trade memes. The Strike Caller trades **emotions**."*

### Recent Performance
- **Win Rate:** 87% (last 30 trades)
- **Average Hold Time:** 2.3 hours
- **Biggest Single Gain:** +1,340% (PEPE)
- **Biggest Loss:** -18% (SHIB)

## For Aspiring Traders

**Best Suited Students:**
- Those who can make decisions in seconds
- Traders comfortable with high volatility
- People who understand social media dynamics
- Students willing to trade smaller, frequent positions

**Warning Signs:**
- If you need hours to decide, this isn't for you
- If you get emotional about losses, step away
- If you can't handle 12-hour trading days, look elsewhere

*Remember: The Strike Caller didn't become legendary overnight. He failed 200 times before that first million-dollar trade.*`,
    },
    accountStats: {
      accountAssets: {
        value: '+$29,345.28',
        percentage: '+54.33%',
        isProfit: true,
      },
      totalPnl: {
        value: '+$9,345.28',
        percentage: '+54.33%',
        isProfit: true,
      },
      winRate: '56.23%',
      profitTrades: 42,
      lossTrades: 28,
      winLossRatio: '1 : 2',
      largestProfit: '+$2,453.55',
      largestLoss: '-$853.55',
      maxDrawdown: '16.33%',
      avgHold: '1d 19h',
      maxHold: '14d 8h',
    },
    portfolioData: generatePortfolioData(25000, true, 0.12),
  },
  {
    id: '2',
    hero: {
      profit: '-$2,156',
      percentage: '-12.67%',
      isProfit: false,
      period: 'Past week',
      badge: {
        type: 'LONG',
        color: 'profit',
      },
    },
    tokens: [
      {
        symbol: 'DOGE',
        type: 'long',
        leverage: '15x',
        pnl: '-$2,890.45',
        percentage: '-23.78%',
        isProfit: false,
      },
      {
        symbol: 'ADA',
        type: 'short',
        leverage: '10x',
        pnl: '+$1,234.56',
        percentage: '+18.92%',
        isProfit: true,
      },
      {
        symbol: 'MATIC',
        type: 'long',
        leverage: '25x',
        pnl: '-$567.89',
        percentage: '-8.45%',
        isProfit: false,
      },
    ],
    user: {
      name: 'Crypto Maverick',
      avatar: 'default-avatar.png',
      address: '0x37a45AdBb275d5d3F8100f4cF16977cd4B0f9Fb7',
    },
    tags: [
      { label: 'DeFi Explorer', bgColor: 'bg-green-500', textColor: 'text-green-400' },
      { label: 'Swing Trader', bgColor: 'bg-yellow-500/20', textColor: 'text-yellow-400' },
      { label: 'Risk Manager', bgColor: 'bg-red-500/20', textColor: 'text-red-400' },
    ],
    story: {
      content:
        '"Despite the market downturn, maintained discipline with strict stop-losses. Lost on DOGE but made it back with ADA shorts. The key is not revenge trading but sticking to the plan."',
    },
    accountStats: {
      accountAssets: {
        value: '+$18,234.56',
        percentage: '-12.67%',
        isProfit: false,
      },
      totalPnl: {
        value: '-$2,156.78',
        percentage: '-12.67%',
        isProfit: false,
      },
      winRate: '45.67%',
      profitTrades: 31,
      lossTrades: 37,
      winLossRatio: '1 : 1.2',
      largestProfit: '+$1,234.56',
      largestLoss: '-$2,890.45',
      maxDrawdown: '23.78%',
      avgHold: '2d 8h',
      maxHold: '7d 12h',
    },
    portfolioData: generatePortfolioData(18000, false, 0.15),
  },
  {
    id: '3',
    hero: {
      profit: '+$15,678',
      percentage: '+89.23%',
      isProfit: true,
      period: 'Past 3 days',
      badge: {
        type: 'LONG',
        color: 'profit',
      },
    },
    tokens: [
      {
        symbol: 'AVAX',
        type: 'long',
        leverage: '30x',
        pnl: '+$8,945.67',
        percentage: '+156.78%',
        isProfit: true,
      },
      {
        symbol: 'LINK',
        type: 'long',
        leverage: '20x',
        pnl: '+$4,567.89',
        percentage: '+67.34%',
        isProfit: true,
      },
      {
        symbol: 'UNI',
        type: 'short',
        leverage: '15x',
        pnl: '+$2,164.44',
        percentage: '+34.56%',
        isProfit: true,
      },
    ],
    user: {
      name: 'Moonshot Hunter',
      avatar: 'default-avatar.png',
      address: '0x8c20e0eddfee07ed6b97c7752b99a41e5a13cb0a',
    },
    tags: [
      { label: 'Altcoin Specialist', bgColor: 'bg-indigo-500/20', textColor: 'text-indigo-400' },
      { label: 'Momentum Trader', bgColor: 'bg-pink-500/20', textColor: 'text-pink-400' },
      { label: 'Pattern Master', bgColor: 'bg-cyan-500/20', textColor: 'text-cyan-400' },
    ],
    story: {
      content:
        '"Caught the perfect altcoin rotation. AVAX breakout was textbook - waited for the retest, entered with size, and rode the wave. Sometimes patience pays off big time."',
    },
    accountStats: {
      accountAssets: {
        value: '+$45,678.90',
        percentage: '+89.23%',
        isProfit: true,
      },
      totalPnl: {
        value: '+$15,678.90',
        percentage: '+89.23%',
        isProfit: true,
      },
      winRate: '78.90%',
      profitTrades: 52,
      lossTrades: 14,
      winLossRatio: '1 : 3.7',
      largestProfit: '+$8,945.67',
      largestLoss: '-$567.89',
      maxDrawdown: '8.45%',
      avgHold: '3d 2h',
      maxHold: '21d 6h',
    },
    portfolioData: generatePortfolioData(32000, true, 0.08),
  },
  {
    id: '4',
    hero: {
      profit: '+$756',
      percentage: '+3.45%',
      isProfit: true,
      period: 'Today',
      badge: {
        type: 'SHORT',
        color: 'loss',
      },
    },
    tokens: [
      {
        symbol: 'XRP',
        type: 'short',
        leverage: '5x',
        pnl: '+$456.78',
        percentage: '+12.34%',
        isProfit: true,
      },
      {
        symbol: 'LTC',
        type: 'long',
        leverage: '8x',
        pnl: '+$299.22',
        percentage: '+5.67%',
        isProfit: true,
      },
      {
        symbol: 'DOT',
        type: 'short',
        leverage: '12x',
        pnl: '+$89.45',
        percentage: '+2.13%',
        isProfit: true,
      },
    ],
    user: {
      name: 'Day Trade Pro',
      avatar: 'default-avatar.png',
      address: '0x4d5e6f7890abcdef123456789012345678ab2c3d',
    },
    tags: [
      { label: 'Scalper', bgColor: 'bg-orange-500/20', textColor: 'text-orange-400' },
      { label: 'News Trader', bgColor: 'bg-emerald-500/20', textColor: 'text-emerald-400' },
      { label: 'Technical Analysis', bgColor: 'bg-violet-500/20', textColor: 'text-violet-400' },
    ],
    story: {
      content:
        '"Small gains today but consistent profits. XRP news reaction was predictable, LTC showing strength, and DOT resistance held. Building the account one trade at a time."',
    },
    accountStats: {
      accountAssets: {
        value: '+$22,456.78',
        percentage: '+3.45%',
        isProfit: true,
      },
      totalPnl: {
        value: '+$756.78',
        percentage: '+3.45%',
        isProfit: true,
      },
      winRate: '62.34%',
      profitTrades: 156,
      lossTrades: 94,
      winLossRatio: '1 : 1.7',
      largestProfit: '+$456.78',
      largestLoss: '-$299.22',
      maxDrawdown: '5.67%',
      avgHold: '4h 15m',
      maxHold: '1d 3h',
    },
    portfolioData: generatePortfolioData(22000, true, 0.05),
  },
  {
    id: '5',
    hero: {
      profit: '-$8,234',
      percentage: '-45.67%',
      isProfit: false,
      period: 'Past 2 weeks',
      badge: {
        type: 'LONG',
        color: 'profit',
      },
    },
    tokens: [
      {
        symbol: 'ATOM',
        type: 'long',
        leverage: '25x',
        pnl: '-$5,678.90',
        percentage: '-78.90%',
        isProfit: false,
      },
      {
        symbol: 'FTM',
        type: 'long',
        leverage: '20x',
        pnl: '-$2,345.67',
        percentage: '-34.56%',
        isProfit: false,
      },
      {
        symbol: 'ALGO',
        type: 'short',
        leverage: '10x',
        pnl: '-$209.43',
        percentage: '-12.34%',
        isProfit: false,
      },
    ],
    user: {
      name: 'Learning Curve',
      avatar: 'default-avatar.png',
      address: '0x5e6f7890abcdef123456789012345678ab2c3d4e',
    },
    tags: [
      { label: 'Student Trader', bgColor: 'bg-slate-500/20', textColor: 'text-slate-400' },
      { label: 'Risk Learning', bgColor: 'bg-amber-500/20', textColor: 'text-amber-400' },
      { label: 'Strategy Testing', bgColor: 'bg-lime-500/20', textColor: 'text-lime-400' },
    ],
    story: {
      content:
        '"Tough lessons learned. Overleveraged on ATOM and got liquidated. Market taught me respect for risk management. Every loss is a lesson if you take the time to learn from it."',
    },
    accountStats: {
      accountAssets: {
        value: '+$9,876.54',
        percentage: '-45.67%',
        isProfit: false,
      },
      totalPnl: {
        value: '-$8,234.56',
        percentage: '-45.67%',
        isProfit: false,
      },
      winRate: '28.90%',
      profitTrades: 13,
      lossTrades: 32,
      winLossRatio: '1 : 0.4',
      largestProfit: '+$1,234.56',
      largestLoss: '-$5,678.90',
      maxDrawdown: '78.90%',
      avgHold: '1d 2h',
      maxHold: '5d 14h',
    },
    portfolioData: generatePortfolioData(12000, false, 0.2),
  },
]
